/**
 * TimelineEventDetails Component
 *
 * Renders expanded content for timeline events.
 * Shows full comment text, photos, task links, etc.
 *
 * @module components/timeline/TimelineEventDetails
 */

import React, { useState, useEffect } from 'react';
import { Spinner, Alert, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getEventDetails } from '../../services/timelineService';
import { formatDate } from '../../utils/formatters';

/**
 * TimelineEventDetails component
 * @param {Object} props
 * @param {Object} props.event - Timeline event data
 * @param {boolean} props.hideImage - When true, Photo source renders metadata only (no duplicated image)
 */
const TimelineEventDetails = ({ event, hideImage = false }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!event?.id || !event?.source) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await getEventDetails(event.id, event.source);

      if (fetchError) {
        setError('Error al cargar detalles');
        console.error('Fetch event details error:', fetchError);
      } else {
        setDetails(data);
      }

      setLoading(false);
    };

    fetchDetails();
  }, [event?.id, event?.source]);

  if (loading) {
    return (
      <div className="timeline-event-details-loading text-center py-3">
        <Spinner animation="border" size="sm" className="me-2" />
        <small className="text-muted">Cargando detalles...</small>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="py-2 mb-0 mt-2">
        <small>{error}</small>
      </Alert>
    );
  }

  // The RPC returns SQL NULL (→ `data: null`, not an error) when the event is
  // not the caller's to read: #500 withholds expense details from the client
  // role, and #506 withholds any event whose project the caller cannot see.
  // A non-existent event id returns NULL too, deliberately (same answer for
  // "doesn't exist" and "not yours", so the RPC is not an existence oracle).
  // Rendering nothing at all made the expanded row look broken, so say
  // something neutral that leaks no information either way.
  if (!details) {
    return (
      <div className="timeline-event-details-empty mt-2">
        <small className="text-muted">Sin detalles disponibles para este evento.</small>
      </div>
    );
  }

  // Render based on event source
  switch (event.source) {
    case 'task_activity':
      return <TaskActivityDetails details={details} event={event} />;
    case 'expense':
      return <ExpenseDetails details={details} />;
    case 'progress_photo':
      return <PhotoDetails details={details} hideImage={hideImage} />;
    default:
      return null;
  }
};

/**
 * Task Activity Details (comments, status changes, etc.)
 */
const TaskActivityDetails = ({ details, event }) => {
  const { task, comment, activity } = details;

  return (
    <div className="timeline-event-expanded mt-3 p-3 bg-light rounded">
      {/* Task link */}
      {task && (
        <div className="mb-2">
          <Link
            to={`/admin/tasks/${task.id}`}
            className="text-decoration-none d-inline-flex align-items-center gap-2"
          >
            <Badge bg="secondary" className="badge-sm">
              {task.task_number}
            </Badge>
            <span className="text-primary">{task.title}</span>
            <Badge
              bg={getStatusColor(task.status)}
              className="badge-sm text-capitalize"
            >
              {task.status}
            </Badge>
          </Link>
        </div>
      )}

      {/* Comment content */}
      {comment && (
        <div className="comment-detail border-start border-3 border-info ps-3 mt-2">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-person-circle text-primary"></i>
            <strong>{comment.user?.full_name || 'Usuario'}</strong>
            <small className="text-muted">
              {new Date(comment.created_at).toLocaleString('es-CR')}
            </small>
          </div>

          {/* Comment text */}
          {comment.comment && (
            <div className="comment-text">
              <ReactMarkdown>{comment.comment}</ReactMarkdown>
            </div>
          )}

          {/* Comment attachments */}
          {comment.attachments && comment.attachments.length > 0 && (
            <div className="comment-attachments-preview mt-2">
              <div className="d-flex flex-wrap gap-2">
                {comment.attachments.map((att, index) => (
                  <a
                    key={index}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-thumb-mini"
                  >
                    <img
                      src={att.url}
                      alt={att.filename || 'Imagen'}
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6',
                      }}
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status change details */}
      {activity?.activity_type === 'status_changed' && activity.metadata && (
        <div className="status-change-detail mt-2">
          <div className="d-flex align-items-center gap-2">
            <Badge bg="secondary">{activity.metadata.old_status}</Badge>
            <i className="bi bi-arrow-right"></i>
            <Badge bg={getStatusColor(activity.metadata.new_status)}>
              {activity.metadata.new_status}
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Expense Details
 */
const ExpenseDetails = ({ details }) => {
  const { expense, user } = details;

  if (!expense) return null;

  return (
    <div className="timeline-event-expanded mt-3 p-3 bg-light rounded">
      <div className="row g-2">
        <div className="col-6">
          <small className="text-muted d-block">Categoria</small>
          <strong>{expense.category}</strong>
        </div>
        <div className="col-6">
          <small className="text-muted d-block">Proveedor</small>
          <strong>{expense.vendor || '-'}</strong>
        </div>
        <div className="col-6">
          <small className="text-muted d-block">Fecha del Gasto</small>
          <strong>{formatDate(expense.expense_date)}</strong>
        </div>
        <div className="col-6">
          <small className="text-muted d-block">Registrado por</small>
          <strong>{user?.full_name || 'Usuario'}</strong>
        </div>
      </div>

      {expense.notes && (
        <div className="mt-2">
          <small className="text-muted d-block">Notas</small>
          <p className="mb-0">{expense.notes}</p>
        </div>
      )}

      {expense.receipt_url && (
        <div className="mt-2">
          <a
            href={expense.receipt_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary"
          >
            <i className="bi bi-file-earmark-pdf me-1"></i>
            Ver Recibo
          </a>
        </div>
      )}
    </div>
  );
};

/**
 * Photo Details
 * @param {Object} props.details - Event details payload
 * @param {boolean} props.hideImage - When true, do not render the image (feed already shows it)
 */
const PhotoDetails = ({ details, hideImage = false }) => {
  const { photo, user } = details;

  if (!photo) return null;

  return (
    <div className="timeline-event-expanded mt-3 p-3 bg-light rounded">
      <div className="photo-detail">
        {!hideImage && (
          <a href={photo.photo_url} target="_blank" rel="noopener noreferrer">
            <img
              src={photo.photo_url}
              alt={photo.caption || 'Foto de progreso'}
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </a>
        )}

        {photo.caption && (
          <p className={`${hideImage ? '' : 'mt-2 '}mb-0 text-muted`}>{photo.caption}</p>
        )}

        <small className="text-muted d-block mt-2">
          Subida por {user?.full_name || 'Usuario'}
          {photo.gps_latitude && photo.gps_longitude && (
            <span className="ms-2">
              <i className="bi bi-geo-alt me-1"></i>
              {photo.gps_latitude.toFixed(4)}, {photo.gps_longitude.toFixed(4)}
            </span>
          )}
        </small>
      </div>
    </div>
  );
};

/**
 * Get status color based on task status
 */
const getStatusColor = (status) => {
  const colors = {
    pending: 'secondary',
    todo: 'secondary',
    in_progress: 'primary',
    review: 'warning',
    completed: 'success',
    cancelled: 'danger',
  };
  return colors[status] || 'secondary';
};

export default TimelineEventDetails;
